import * as React from "react";
import { cn } from "./utils";

export interface SwitchProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onCheckedChange?: (checked: boolean) => void;
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ onCheckedChange, onChange, ...rest }, ref) => (
    <input
      ref={ref}
      type="checkbox"
      role="switch"
      onChange={(e) => {
        onChange?.(e);
        onCheckedChange?.(e.currentTarget.checked);
      }}
      {...rest}
    />
  ),
);
Switch.displayName = "Switch";

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  indeterminate?: boolean;
  label?: React.ReactNode;
  onCheckedChange?: (checked: boolean) => void;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      indeterminate = false,
      label,
      id,
      disabled,
      onChange,
      onCheckedChange,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <label
        htmlFor={inputId}
        className={cn(
          "inline-flex items-center gap-2 text-foreground",
          disabled && "opacity-50",
          className,
        )}
      >
        <input
          id={inputId}
          ref={inputRef}
          type="checkbox"
          disabled={disabled}
          className="h-4 w-4 rounded border border-border"
          onChange={(e) => {
            onChange?.(e);
            onCheckedChange?.(e.currentTarget.checked);
          }}
          {...props}
        />
        {label ? <span className="select-none text-sm">{label}</span> : null}
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

export const Label = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement>
>(({ className, ...props }, ref) => (
  <label
    ref={ref}
    className={cn(
      "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
      className,
    )}
    {...props}
  />
));
Label.displayName = "Label";
